import { useEffect, useState } from 'react';
import {
  FiActivity,
  FiArchive,
  FiClipboard,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiFolder,
  FiImage,
  FiMessageSquare,
  FiPaperclip,
  FiCalendar,
  FiShield,
} from 'react-icons/fi';
import { getSessionUser, SESSION_USER_EVENT } from './session';

// Every section of the platform. Who sees what is decided by the server
// (server/utils/permissions.js) and delivered with the session as `rights`.
export const SECTIONS = [
  { id: 'patients', title: 'Dossiers patients', description: 'Liste, fiches et suivi des patients.', path: '/patients', icon: FiFolder, tint: 'bg-brand-600', group: 'Soins' },
  { id: 'staff', title: 'Staff', description: 'Observations du staff de votre service.', path: '/staff', icon: FiClipboard, tint: 'bg-teal-600', group: 'Soins' },
  { id: 'programme', title: 'Programme opératoire', description: 'Documents du programme du bloc.', path: '/programme', icon: FiCalendar, tint: 'bg-violet-600', group: 'Soins' },
  { id: 'echo', title: 'Échographies', description: 'Fiches d’échocardiographie.', path: '/echo', icon: FiActivity, tint: 'bg-rose-500', group: 'Soins' },
  { id: 'cro', title: 'Comptes rendus opératoires', description: 'CRO des interventions.', path: '/cro', icon: FiFileText, tint: 'bg-orange-500', group: 'Soins' },
  // Managed inside each patient file (block "Fichiers"): a right, not a page of the menu.
  { id: 'fichiers', title: 'Fichiers patients', description: 'Documents joints aux dossiers.', path: '/patients', icon: FiPaperclip, tint: 'bg-sky-600', group: 'Soins', menu: false },
  { id: 'messages', title: 'Messages', description: 'Échanges de l’équipe du service.', path: '/message', icon: FiMessageSquare, tint: 'bg-emerald-600', group: 'Équipe' },
  { id: 'gardes', title: 'Gardes et astreintes', description: 'Tableaux des gardes et astreintes.', path: '/garde', icon: FiClock, tint: 'bg-amber-500', group: 'Équipe' },
  { id: 'iconographie', title: 'Iconographie', description: 'Imagerie partagée par l’équipe.', path: '/iconographie', icon: FiImage, tint: 'bg-indigo-500', group: 'Équipe' },
  { id: 'archives', title: 'Archives', description: 'Dossiers archivés, avec votre code d’accès.', path: '/archive', icon: FiArchive, tint: 'bg-slate-600', group: 'Espaces protégés', locked: true },
  { id: 'payments', title: 'Paiements', description: 'Solvabilité et paiements, avec votre code d’accès.', path: '/patientsolvable', icon: FiCreditCard, tint: 'bg-lime-600', group: 'Espaces protégés', locked: true },
  { id: 'admin', title: 'Administration', description: 'Comptes du personnel et journal d’activité.', path: '/usermanagement', icon: FiShield, tint: 'bg-ink', group: 'Gestion' },
];

export const GROUPS = ['Soins', 'Équipe', 'Espaces protégés', 'Gestion'];

const ACTIONS = ['voir', 'modifier', 'supprimer', 'tout'];

// ----- Fallback only: same grid as server/utils/permissions.js, used while the server in
// use does not send `rights` yet (client deployed before the server). Keep both in step.
const LEVEL_ACTION = {
  Affichage: 'voir',
  'Affichage-Modification': 'modifier',
  'Affichage-Modification-Suppression': 'supprimer',
  Administrateur: 'tout',
};
const ROLE_SECTIONS = {
  Médecin: ['patients', 'programme', 'staff', 'echo', 'cro', 'fichiers', 'messages', 'iconographie', 'gardes'],
  Secrétaire: ['patients', 'programme', 'staff', 'cro', 'fichiers', 'messages', 'iconographie', 'gardes'],
  'Infirmier(e)': ['patients', 'programme', 'iconographie', 'gardes'],
  Archiviste: ['patients', 'programme', 'iconographie', 'gardes'],
  Gestionnaire: ['patients', 'programme', 'iconographie', 'gardes'],
  'Etudiant(e)': ['patients', 'iconographie', 'gardes'],
};
const defaultAreas = ({ role, accessLevel }) => {
  const areas = [];
  if (['Admin', 'Gestionnaire'].includes(role) || (['Médecin', 'Secrétaire'].includes(role) && accessLevel === 'Administrateur')) {
    areas.push('payments');
  }
  if (['Admin', 'Archiviste', 'Médecin'].includes(role)) areas.push('archives');
  return areas;
};
const lowest = (a, b) => (ACTIONS.indexOf(a) <= ACTIONS.indexOf(b) ? a : b);

const fallbackRights = (user) => {
  if (!user || !user.role) return {};
  if (user.role === 'Admin') return Object.fromEntries(SECTIONS.map((section) => [section.id, 'tout']));
  const levelAction = LEVEL_ACTION[user.accessLevel] || 'voir';
  const areas = Array.isArray(user.areas) ? user.areas : defaultAreas(user);
  const sections = [...(ROLE_SECTIONS[user.role] || []), ...areas];
  if (user.role === 'Secrétaire' && unitsOf(user.service).includes('cardiologie')) sections.push('echo');
  return Object.fromEntries(
    sections.map((section) => {
      let cap = 'tout';
      if (user.role === 'Etudiant(e)') cap = 'voir';
      if (section === 'patients' && ['Infirmier(e)', 'Archiviste', 'Gestionnaire'].includes(user.role)) cap = 'voir';
      if ((section === 'cro' || section === 'fichiers') && user.role === 'Secrétaire') cap = 'voir';
      if (section === 'programme' && user.role !== 'Secrétaire') cap = 'voir';
      if (section === 'archives' && user.role !== 'Archiviste') cap = 'voir';
      if (user.role === 'Secrétaire' && section === 'staff' && ACTIONS.indexOf(levelAction) < ACTIONS.indexOf('supprimer')) cap = 'voir';
      return [section, lowest(levelAction, cap)];
    })
  );
};

// Rights sent by the server with the account, or the fallback above.
export const rightsOf = (user) => (user && user.rights) || fallbackRights(user);

export const getRights = () => rightsOf(getSessionUser());

export const canSee = (section) => Boolean(getRights()[section]);

// allows(rights, 'staff', 'modifier'): same check on rights already read (rightsOf(user)).
export const allows = (rights, section, action) =>
  Boolean(rights && rights[section]) && ACTIONS.indexOf(rights[section]) >= ACTIONS.indexOf(action);

// can('echo', 'supprimer'): the level reached on the account includes the action.
export const can = (section, action) => {
  const allowed = getRights()[section];
  return Boolean(allowed) && ACTIONS.indexOf(allowed) >= ACTIONS.indexOf(action);
};

export const visibleSections = () => SECTIONS.filter((section) => canSee(section.id));

// Units a staff member works in (same rule as the server): "Cuomo-Cardiologie" covers both.
export const unitsOf = (service = '') =>
  String(service || '')
    .split('-')
    .map((unit) => unit.trim().toLowerCase())
    .filter(Boolean);

// Outside the administration, a patient file is modified by its own unit only.
export const worksOnUnit = (user, unit) =>
  Boolean(user) && (user.role === 'Admin' || unitsOf(user.service).includes(String(unit || '').trim().toLowerCase()));

// The signed-in account, kept up to date when the server sends new permissions.
export const useSessionUser = () => {
  const [user, setUser] = useState(() => getSessionUser() || {});
  useEffect(() => {
    const update = () => setUser(getSessionUser() || {});
    window.addEventListener(SESSION_USER_EVENT, update);
    return () => window.removeEventListener(SESSION_USER_EVENT, update);
  }, []);
  return user;
};
